/**
 * The root shell's tenant settings, moved from the shell's `services/settings-service.ts` (kernel
 * apps, WS-14 section 3.6; layered shell surfaces, R-35, R-60, R-22's `settings` member).
 *
 * The CP writes an org's `shell.settings` and syncs it to core-api, which serves it at
 * `GET /v1/root-shell/settings`. The host reads it through a {@link ShellSettingsSource} and
 * serves it to the routed root mount alone; every other mount reads `settings: null`. The value is
 * parsed with `ShellSettingsSchema` from `@zion/sdk-cp-dp-contracts`, so a malformed answer never
 * reaches a surface.
 */
import { ShellSettingsSchema } from '@zion/sdk-cp-dp-contracts';
import { GET_ROOT_SHELL_SETTINGS_ROUTE, getRootShellSettingsResponseSchema } from '@zion/sdk-dp-admin-contracts';
import type { SurfaceSettingsService, SurfaceSettingsState } from '@zion/sdk-surfaces/v1';
import { createBodyDeadline } from '@zion/shell-system-surfaces/body-deadline';
import { ApiError, SERVICES_FETCH_TIMEOUT_MS } from './fetch-policy.js';
import { createListenerSet, type MountScope, type ServiceSession, type ServiceSessionSnapshot } from './kit.js';
import { isRoutedRoot } from './polarity.js';

/**
 * Loads the org's settings object for one session snapshot, or rejects. The host supplies it;
 * the answer is untrusted and the controller parses it.
 */
export type ShellSettingsSource = (session: ServiceSessionSnapshot) => Promise<unknown>;

/**
 * The settings source over `GET /v1/root-shell/settings` (R-60), sent through the policy's
 * `platformFetch` (WS-14 section 3.6, change 3).
 *
 * @param platformFetch The policy's `platformFetch`. It attaches the live session bearer at each
 *   attempt and bounds the wait for the headers, so a retry after a token rotation carries the new
 *   token. The source sets no `Authorization` header itself.
 * @param timeoutMs The bound on the body read after the headers, in milliseconds;
 *   {@link SERVICES_FETCH_TIMEOUT_MS} unless a test shortens it.
 * @returns A source that answers per session snapshot:
 *   - hydrating, anonymous, or authenticated with no token: resolves `null` and sends nothing,
 *     because the route answers 401 to a caller with no session;
 *   - authenticated: sends one same-origin `GET /v1/root-shell/settings`. A 2xx body that passes
 *     `getRootShellSettingsResponseSchema` resolves its `settings` member, an object or `null`.
 *
 * Errors: a non-2xx answer rejects with {@link ApiError} carrying the status; a body that is not
 * JSON, or not that schema, rejects with a `TypeError`; a body that does not end within
 * `timeoutMs` after the headers rejects with a `TimeoutError`; a `platformFetch` rejection (its
 * header deadline included) rejects with that error. The controller reads every rejection as
 * `settings: null`. The route takes no input, so no session value reaches the URL.
 */
export function createRootShellSettingsSource(
  platformFetch: typeof fetch,
  timeoutMs: number = SERVICES_FETCH_TIMEOUT_MS,
): ShellSettingsSource {
  return async (snapshot) => {
    if (snapshot.status !== 'authenticated' || snapshot.token === null) return null;
    const deadline = createBodyDeadline(timeoutMs);
    const res = await platformFetch(GET_ROOT_SHELL_SETTINGS_ROUTE.path, {
      method: GET_ROOT_SHELL_SETTINGS_ROUTE.method,
      headers: { Accept: 'application/json' },
      signal: deadline.signal,
    });
    if (!res.ok) {
      throw new ApiError(res.status, `${GET_ROOT_SHELL_SETTINGS_ROUTE.path} answered ${res.status}`, null);
    }
    let body: unknown;
    try {
      body = await deadline.read<unknown>(() => res.json());
    } catch (err) {
      // A body past the deadline is a `TimeoutError`; a body that is not JSON reads as off-schema.
      if (deadline.signal.aborted) throw err;
      body = undefined;
    }
    const parsed = getRootShellSettingsResponseSchema.safeParse(body);
    if (!parsed.success) {
      throw new TypeError(`${GET_ROOT_SHELL_SETTINGS_ROUTE.path} answered a body outside its schema`);
    }
    return parsed.data.settings;
  };
}

/**
 * The document-wide settings owner.
 *
 * - With no source, `current()` is `{ settings: null, resolved: true }` from creation and never
 *   changes.
 * - With a source, `current()` starts `{ settings: null, resolved: false }`. While the session
 *   hydrates nothing loads. On the first non-hydrating snapshot, and on every later change of
 *   `status`, `userId` or `orgId`, it calls the source once. On such a change it first sets
 *   `settings` to `null` and keeps `resolved`, and notifies when the value it dropped was not
 *   `null`. So a new identity never reads the settings of the identity before it while its own
 *   load is in flight. An answer that passes `ShellSettingsSchema` becomes a frozen copy; a
 *   refusal, a rejection or a synchronous throw reads `null`. A response overtaken by a later
 *   load writes nothing. `resolved` turns `true` at the first settle and stays `true`. Each state
 *   change notifies once; a `null` that follows a resolved `null` notifies nobody.
 * - `view(scope)` is the mount's {@link SurfaceSettingsService}: the routed root reads the state;
 *   any other mount reads `{ settings: null, resolved }`, whose identity changes only with
 *   `resolved`. Subscriptions end with the mount, and after the mount ends both members throw
 *   `disposed`.
 * - `dispose()` stops the session subscription, drops every subscriber and makes a load in flight
 *   write nothing.
 */
export interface SettingsController {
  current(): SurfaceSettingsState;
  subscribe(listener: () => void): () => void;
  view(scope: MountScope): SurfaceSettingsService;
  dispose(): void;
}

/**
 * Build the controller.
 *
 * @param session This origin's session port.
 * @param source The org's settings loader, or `null` for a host with no settings (WS-14 section
 *   3.6, change 4).
 */
export function createSettingsController(
  session: ServiceSession,
  source: ShellSettingsSource | null,
): SettingsController {
  const listeners = createListenerSet();
  let state: SurfaceSettingsState = Object.freeze({ settings: null, resolved: source === null });
  let identity: string | null = null;
  let generation = 0;
  let disposed = false;

  const load = (): void => {
    if (source === null) return;
    const snapshot = session.current();
    if (snapshot.status === 'hydrating') return;
    const key = `${snapshot.status}\n${snapshot.userId ?? ''}\n${snapshot.orgId ?? ''}`;
    if (key === identity) return;
    identity = key;
    generation += 1;
    if (state.settings !== null) {
      state = Object.freeze({ settings: null, resolved: state.resolved });
      listeners.notify();
    }
    const captured = generation;
    void Promise.resolve()
      .then(() => source(snapshot))
      .then(
        (answer) => {
          const parsed = ShellSettingsSchema.safeParse(answer);
          return parsed.success ? Object.freeze({ ...parsed.data }) : null;
        },
        () => null,
      )
      .then((settings) => {
        if (disposed || captured !== generation) return;
        if (state.resolved && state.settings === null && settings === null) return;
        state = Object.freeze({ settings, resolved: true });
        listeners.notify();
      });
  };

  const stopSession = session.subscribe(load);
  load();

  return {
    current: () => state,
    subscribe: (listener) => listeners.add(listener),
    view(scope) {
      const root = isRoutedRoot(scope);
      let blank: SurfaceSettingsState = Object.freeze({ settings: null, resolved: state.resolved });
      const viewCurrent = (): SurfaceSettingsState => {
        if (root) return state;
        if (blank.resolved !== state.resolved) blank = Object.freeze({ settings: null, resolved: state.resolved });
        return blank;
      };
      return scope.guard({
        current: viewCurrent,
        subscribe: (listener: () => void) => {
          let seen = viewCurrent();
          const remove = listeners.add(() => {
            const next = viewCurrent();
            if (next === seen) return;
            seen = next;
            listener();
          });
          scope.own(remove);
          return remove;
        },
      });
    },
    dispose() {
      disposed = true;
      stopSession();
      listeners.clear();
    },
  };
}
